import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPageBySlug } from '../lib/dataService';
import RegistrationForm from '../components/RegistrationForm';
import ProgramRegistrationForm from '../components/ProgramRegistrationForm';
import IndividualRegistrationForm from '../components/IndividualRegistrationForm';
import { Users, User } from 'lucide-react';

// League pages that carry the team registration form, with a sensible default league
const REGISTRATION_PAGES = {
  'national-mens-basketball-league': '',
  'national-womens-league': "Senior Women's League",
  'under16': '',
  'under-19': '',
  'ciba-15-under-boys-girls-basketball-leagues': '',
  'corporate-basketball-league': 'Corporate Basketball League',
  'under-13': '',
};

// League pages that also offer an individual (no team) player sign-up
const INDIVIDUAL_REGISTRATION_PAGES = {
  'under-13': { label: '13 & Under League', divisions: ['Under 13 Boys', 'Under 13 Girls'] },
  'under-10': { label: '10 & Under League', divisions: ['Under 10 Boys', 'Under 10 Girls'] },
};

// Youth program pages that carry the child registration form
const PROGRAM_REGISTRATION_PAGES = {
  'kings-development-program': { program: 'kings', label: 'Kings Development Program' },
  'academy': { program: 'academy', label: 'Basketball Academy' },
};

export default function StaticPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [regMode, setRegMode] = useState('team');

  useEffect(() => {
    setPage(null);
    setNotFound(false);
    setRegMode('team');
    getPageBySlug(slug).then((p) => {
      if (!p) { setNotFound(true); return; }
      setPage(p);
    });
  }, [slug]);

  if (notFound) {
    return (
      <div className="container section article-notfound">
        <h1>Page not found</h1>
        <Link to="/" className="btn-primary">Back Home</Link>
      </div>
    );
  }

  if (!page) return <div className="container section"><div className="loading-block" /></div>;

  const isEmpty = !page.content || page.content.trim().length === 0;
  const hasTeamForm = slug in REGISTRATION_PAGES;
  const hasIndividualForm = slug in INDIVIDUAL_REGISTRATION_PAGES;

  return (
    <div className="static-page">
      <div className="page-hero">
        <div className="container">
          <span className="page-hero-label">Cayman Islands Basketball Association</span>
          <h1>{page.title}</h1>
        </div>
      </div>
      <div className="container article-body-wrap section">
        {isEmpty ? (
          <p className="empty-state">Content for this page is coming soon. Contact us at <a href="mailto:info@caymanbasketball.com">info@caymanbasketball.com</a> for more information.</p>
        ) : (
          <div className="wp-content" dangerouslySetInnerHTML={{ __html: page.content }} />
        )}
        {hasTeamForm && hasIndividualForm ? (
          <div className="reg-mode-wrap" id="register">
            <div className="reg-mode-toggle">
              <button
                type="button"
                className={regMode === 'team' ? 'active' : ''}
                onClick={() => setRegMode('team')}
                data-testid="reg-mode-team-btn"
              >
                <Users size={16} /> Register a Team
              </button>
              <button
                type="button"
                className={regMode === 'individual' ? 'active' : ''}
                onClick={() => setRegMode('individual')}
                data-testid="reg-mode-individual-btn"
              >
                <User size={16} /> Individual Player Sign-Up
              </button>
            </div>
            <p className="reg-mode-note">
              Have a team or club? Register your full team below. Don't have a team? Use the
              Individual Player Sign-Up and we will place your child on a team.
            </p>
            {regMode === 'team' ? (
              <RegistrationForm defaultLeague={REGISTRATION_PAGES[slug]} pageSlug={slug} />
            ) : (
              <IndividualRegistrationForm
                leagueLabel={INDIVIDUAL_REGISTRATION_PAGES[slug].label}
                divisions={INDIVIDUAL_REGISTRATION_PAGES[slug].divisions}
                pageSlug={slug}
              />
            )}
          </div>
        ) : hasTeamForm ? (
          <RegistrationForm defaultLeague={REGISTRATION_PAGES[slug]} pageSlug={slug} />
        ) : hasIndividualForm ? (
          <div className="reg-mode-wrap" id="register">
            <IndividualRegistrationForm
              leagueLabel={INDIVIDUAL_REGISTRATION_PAGES[slug].label}
              divisions={INDIVIDUAL_REGISTRATION_PAGES[slug].divisions}
              pageSlug={slug}
            />
          </div>
        ) : null}
        {slug in PROGRAM_REGISTRATION_PAGES && (
          <ProgramRegistrationForm
            program={PROGRAM_REGISTRATION_PAGES[slug].program}
            programLabel={PROGRAM_REGISTRATION_PAGES[slug].label}
          />
        )}
      </div>
    </div>
  );
}
